import type { ComponentProps } from 'react';
import type { ClerkProvider } from '@clerk/clerk-react';

/**
 * How this bundle signs a teacher in — the two public browser credentials and the redirect
 * rules, in one file so `App.tsx` reads as routing rather than as configuration.
 *
 * The shape is `packages/objectives/src/App.tsx`'s, lifted out of the component because
 * Collab has a second entry point — `/join` — and a rule about where sign-in returns to
 * should not be written twice.
 */

export const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

/**
 * The browser-test identities.
 *
 * When set, they win — including over a compiled-in publishable key. That is deliberate and
 * it is not the security boundary: which component tree renders cannot grant anyone access
 * to anything. The Worker decides, and it honours a bypass token only when no
 * `CLERK_SECRET_KEY` and no issuer exist, so a production build refuses these tokens no
 * matter what the browser rendered.
 *
 * Each entry is `user_id:email:verified`, comma-separated, and the list must be the same one
 * `CLERK_TEST_BYPASS` carries in `.dev.vars.e2e` — the browser half and the Worker half of
 * the same hatch. The trailing marker is the verification claim KTD14 requires; an entry
 * without it is unverified and can never accept an invitation.
 *
 * It is a *list* because U10's membership boundary cannot be proven by one identity. An
 * admin, an invited member and an outsider have to be three different people in one run, and
 * the identity is compiled into the bundle — so all three are compiled in and the tab picks
 * one. Which one is a runtime choice (`E2E_IDENTITY_KEY`), because a bundle per identity
 * would mean a Worker per identity and no shared database between them.
 */
export const e2eIdentities: readonly string[] = parseIdentities(import.meta.env.VITE_E2E_IDENTITY);

/** Pure: the trimmed, non-empty entries of a `VITE_E2E_IDENTITY` value. */
export function parseIdentities(raw: string | undefined): readonly string[] {
  return (raw ?? '').split(',').map((entry) => entry.trim()).filter((entry) => entry.length > 0);
}

/**
 * Which identity this tab is signed in as, held in `sessionStorage`.
 *
 * Per tab, exactly like the invitation token beside it — one tab is one teacher, which is
 * what lets three browser contexts drive three teachers against one Worker. It dies with the
 * tab, so nothing a test leaves behind reaches the next one.
 */
export const E2E_IDENTITY_KEY = 'collab.e2e-identity';

/**
 * Pure: the entry this tab is signed in as, or null for "signed out".
 *
 * A single compiled-in identity needs no choice — that is `packages/objectives`' recipe and
 * a developer building with one `VITE_E2E_IDENTITY` should still land inside the app. With
 * more than one, an unchosen tab is signed out on purpose: it is the state the signed-out
 * invitee scenario needs, and guessing a default there would silently sign the browser in as
 * whoever happened to be first in the list.
 *
 * A stored value is matched against the compiled-in list rather than trusted, so an entry
 * left over from an older build cannot become a token the Worker has never heard of.
 */
export function selectE2eIdentity(
  identities: readonly string[],
  chosen: string | null,
): string | null {
  if (identities.length === 0) return null;
  if (chosen === null) return identities.length === 1 ? identities[0] ?? null : null;
  return identities.find((entry) => entry === chosen || entry.split(':')[0] === chosen) ?? null;
}

/** Pure: the address in an entry, for the sign-in control that names it. */
export const emailOfIdentity = (entry: string): string => {
  const [userId = '', email = ''] = entry.split(':');
  return email.length > 0 ? email : userId;
};

/** The one impure read. Null whenever storage is unavailable, exactly like `invite-token`. */
export function chosenE2eIdentity(): string | null {
  try {
    return window.sessionStorage.getItem(E2E_IDENTITY_KEY);
  } catch {
    return null;
  }
}

/**
 * Sign this tab in as one of the compiled-in identities, the way Clerk would: store, then
 * *reload*.
 *
 * The reload is the part that matters. A signed-out invitee who opens a link goes through a
 * full page load to come back, and a fragment does not survive that trip — which is the whole
 * reason `invite-token.ts` holds the token in `sessionStorage`. Setting React state instead
 * would sign the teacher in without ever making that round trip, and the suite would prove
 * nothing about the path the real invitee takes.
 */
export function signInAsE2eIdentity(entry: string): void {
  try {
    window.sessionStorage.setItem(E2E_IDENTITY_KEY, entry);
  } catch {
    // Storage refused — a private window. Nothing to recover: without a store there is no
    // way to survive the reload, and this hatch exists only for the browser suite.
  }
  window.location.reload();
}

/**
 * Force, not fallback, and all four of them.
 *
 * With none of these set, Clerk uses whatever the *instance* is configured to do — and 240's
 * production instance sends people to study.240tutoring.com/courses, because that is what
 * the study platform needs. `fallback` variants only apply when no redirect is already in
 * play, so they lose to the instance default; `force` is the one that keeps a Collab user in
 * Collab. This is the launch bug `packages/objectives` already paid for.
 *
 * The invitee returns to `/`, not to `/join`, and that is correct: the fragment their link
 * carried is long gone by then (`invite-token.ts` strips it before this provider mounts), so
 * `/join` would be an empty page. The token is held in `sessionStorage` instead, and `App`
 * renders the join screen wherever the teacher lands as long as one is held.
 */
export const clerkRedirects = {
  signInForceRedirectUrl: '/',
  signUpForceRedirectUrl: '/',
  signInFallbackRedirectUrl: '/',
  signUpFallbackRedirectUrl: '/',
  afterSignOutUrl: '/',
  // Off, so the CSP does not have to allow clerk-telemetry.com. A teacher's group activity
  // is not something to send to a vendor's analytics on our behalf, and with it on the
  // console fills with blocked-request noise that hides real failures.
  telemetry: false,
} satisfies Partial<ComponentProps<typeof ClerkProvider>>;
